import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import { Card } from "design-system/molecules/Card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "design-system/molecules/DropdownMenu"
import { MapPin, Plus, Star, Trash2 } from "lucide-react"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { ReviewProvider } from "schema"

type RequestReviewFieldsConfig = {
  providers: ReviewProvider[]
}

type Props = {
  value: RequestReviewFieldsConfig
  onChange: (value: RequestReviewFieldsConfig) => void
}

export function RequestReviewFields({ value, onChange }: Props) {
  const { t } = useTranslation()

  const addGoogleMapsProvider = useCallback(() => {
    onChange({
      providers: [
        ...value.providers,
        {
          id: crypto.randomUUID(),
          type: "google_maps" as const,
          scope: "venue" as const,
          label: "",
          link: "",
        },
      ],
    })
  }, [value, onChange])

  const addTrustpilotProvider = useCallback(() => {
    onChange({
      providers: [
        ...value.providers,
        {
          id: crypto.randomUUID(),
          type: "trustpilot" as const,
          scope: "global" as const,
          link: "",
        },
      ],
    })
  }, [value, onChange])

  const updateProvider = useCallback(
    (index: number, updated: ReviewProvider) => {
      const providers = [...value.providers]
      providers[index] = updated
      onChange({ providers })
    },
    [value, onChange],
  )

  const removeProvider = useCallback(
    (index: number) => {
      onChange({ providers: value.providers.filter((_, i) => i !== index) })
    },
    [value, onChange],
  )

  const hasTrustpilot = value.providers.some((p) => p.type === "trustpilot")

  return (
    <div className="flex flex-col gap-3">
      <div>
        <p className="text-sm font-medium">
          {t("capabilities.catalog.RequestReview.config.providers.title")}
        </p>
        <p className="text-sm text-muted-foreground mt-0.5">
          {t("capabilities.catalog.RequestReview.config.providers.description")}
        </p>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="sm" className="self-start">
            <Plus className="h-4 w-4" />
            {t("capabilities.catalog.RequestReview.config.providers.add")}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={addGoogleMapsProvider}>
            <MapPin className="h-4 w-4" />
            {t("capabilities.catalog.RequestReview.config.providers.addGoogleMaps")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={addTrustpilotProvider} disabled={hasTrustpilot}>
            <Star className="h-4 w-4" />
            {t("capabilities.catalog.RequestReview.config.providers.addTrustpilot")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {value.providers.map((provider, index) => (
        <ProviderCard
          key={provider.id}
          provider={provider}
          index={index}
          onUpdate={updateProvider}
          onRemove={removeProvider}
        />
      ))}
    </div>
  )
}

type ProviderCardProps = {
  provider: ReviewProvider
  index: number
  onUpdate: (index: number, updated: ReviewProvider) => void
  onRemove: (index: number) => void
}

function ProviderCard({ provider, index, onUpdate, onRemove }: ProviderCardProps) {
  const { t } = useTranslation()

  const headerLabel =
    provider.type === "google_maps"
      ? t("capabilities.catalog.RequestReview.config.providers.googleMaps.label")
      : t("capabilities.catalog.RequestReview.config.providers.trustpilot.label")

  const Icon = provider.type === "google_maps" ? MapPin : Star

  return (
    <Card>
      <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">{headerLabel}</span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onRemove(index)}
          title={t("shared.delete")}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="p-3 flex flex-col gap-3 bg-background">
        {provider.type === "google_maps" && (
          <div className="flex flex-col gap-1.5">
            <Label>
              {t("capabilities.catalog.RequestReview.config.providers.googleMaps.venue.label")}
            </Label>
            <Input
              value={provider.label}
              onChange={(e) => onUpdate(index, { ...provider, label: e.target.value })}
              placeholder={t(
                "capabilities.catalog.RequestReview.config.providers.googleMaps.venue.placeholder",
              )}
            />
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <Label>{t("capabilities.catalog.RequestReview.config.providers.link.label")}</Label>
          <Input
            value={provider.link}
            onChange={(e) => onUpdate(index, { ...provider, link: e.target.value })}
            placeholder={
              provider.type === "google_maps"
                ? t(
                    "capabilities.catalog.RequestReview.config.providers.googleMaps.link.placeholder",
                  )
                : t(
                    "capabilities.catalog.RequestReview.config.providers.trustpilot.link.placeholder",
                  )
            }
          />
        </div>
      </div>
    </Card>
  )
}
